/**
 * Page-level building blocks: the two tab bars and the white section card.
 */
import { motion, useReducedMotion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { PlaneIcon, TrainIcon } from "../../../../components/icons";
import { Reveal, transitions } from "../../../../components/motion";
import { cn } from "../../../../utils/classnames";
import { GROUP_AIRPORT, GROUP_STATION, JOURNEYS_BY_GROUP } from "../constants";

const ACTIVE_TAB_COLOR = "#4E4E4E";
const INACTIVE_TAB_COLOR = "#8D8D8D";

/**
 * TrainIcon's drawing is 12x14 inside a 16x16 box, pinned to the top-left.
 * This viewBox pads it evenly so it sits centred like the plane.
 */
const CENTERED_TRAIN_VIEWBOX = "-2 -1 16 16";

const GROUP_TABS = [
  { group: GROUP_AIRPORT, Icon: PlaneIcon, iconProps: {} },
  { group: GROUP_STATION, Icon: TrainIcon, iconProps: { viewBox: CENTERED_TRAIN_VIEWBOX } },
];

/**
 * The spring that moves a tab highlight to the tab that was clicked.
 * With reduced motion the highlight jumps instead.
 */
const useHighlightTransition = () => {
  const shouldReduceMotion = useReducedMotion();
  return shouldReduceMotion ? { duration: 0 } : transitions.spring;
};

/**
 * Airport / Station switch under the hero. Folder-tab look: the active tab is
 * open at the bottom so it joins the grey page below.
 * The open card is one element shared by both tabs (same `layoutId`), so it
 * glides across when the guest switches instead of jumping.
 * Phones show the icon above a two-line label; desktop puts them in one row.
 */
export const GroupTabs = ({ activeGroup, onSelectGroup }) => {
  const { t } = useTranslation();
  const highlightTransition = useHighlightTransition();

  return (
    <div className="tw:bg-[#F1F1F1] tw:border-b tw:border-solid tw:border-[#8D8D8D] tw:px-3 tw:lg:px-[82px] tw:pt-[23px] tw:lg:pt-[19px]">
      <div role="tablist" className="tw:flex tw:-mb-px tw:max-w-[1276px] tw:mx-auto">
        {GROUP_TABS.map(({ group, Icon, iconProps }) => {
          const isActive = group === activeGroup;
          return (
            <button
              key={group}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => onSelectGroup(group)}
              className={cn(
                "tw:relative tw:flex-1 tw:h-[97px] tw:lg:h-[73px] tw:!px-3 tw:bg-transparent tw:border-0 tw:font-hanken-grotesk tw:font-medium tw:transition-colors tw:duration-300",
                isActive ? "tw:text-[#282828]" : "tw:text-[#8D8D8D] tw:hover:text-[#4E4E4E]"
              )}
            >
              {isActive && (
                <motion.span
                  layoutId="meetAssistGroupTabCard"
                  transition={highlightTransition}
                  className="tw:absolute tw:inset-0 tw:bg-[#F9F9F9] tw:border tw:border-b-0 tw:border-solid tw:border-[#8D8D8D] tw:!rounded-t-[5px]"
                />
              )}
              <span className="tw:relative tw:h-full tw:flex tw:flex-col tw:lg:flex-row tw:items-center tw:justify-center tw:gap-[10px] tw:lg:gap-3">
                <Icon
                  {...iconProps}
                  color={isActive ? ACTIVE_TAB_COLOR : INACTIVE_TAB_COLOR}
                  className="tw:size-[15px] tw:lg:size-5"
                  aria-hidden="true"
                />
                <span className="tw:text-center tw:text-[14px] tw:leading-[20px] tw:lg:text-[16px] tw:lg:leading-normal">
                  <span className="tw:block tw:lg:inline">{t(`meetAssistInquiry.Groups.${group}`)}</span>{" "}
                  <span className="tw:block tw:lg:inline">{t("meetAssistInquiry.Groups.Suffix")}</span>
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

/**
 * Arrival / Departure / Connecting Flights tabs on top of the first card.
 *
 * Every tab sits on one grey strip; the active tab is a taller white card
 * (shared `layoutId`) that slides to whichever tab is clicked. Each label box
 * grows or shrinks with its tab, so the text rises into the white card.
 */
export const JourneyTabs = ({ group, activeJourney, onSelectJourney }) => {
  const { t } = useTranslation();
  const highlightTransition = useHighlightTransition();

  return (
    <div
      role="tablist"
      className="tw:relative tw:flex tw:items-end tw:h-[68px] tw:lg:h-[75px] tw:border-b tw:border-solid tw:border-[#D9D9D9]"
    >
      {/* The grey strip the inactive tabs sit on. */}
      <span
        aria-hidden="true"
        className="tw:absolute tw:inset-x-0 tw:bottom-0 tw:h-[54px] tw:lg:h-[60px] tw:bg-[#F1F1F1] tw:!rounded-t-[5px]"
      />

      {JOURNEYS_BY_GROUP[group].map((journey) => {
        const isActive = journey === activeJourney;
        return (
          <button
            key={journey}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onSelectJourney(journey)}
            className="tw:relative tw:flex-auto tw:lg:flex-1 tw:h-full tw:flex tw:items-end tw:bg-transparent tw:border-0 tw:!p-0 tw:whitespace-nowrap tw:font-hanken-grotesk"
          >
            {isActive && (
              <motion.span
                layoutId="meetAssistJourneyTabCard"
                transition={highlightTransition}
                className="tw:absolute tw:inset-0 tw:bg-white tw:border tw:border-b-0 tw:border-solid tw:border-[#F1F1F1] tw:!rounded-t-[5px]"
              />
            )}
            <span
              className={cn(
                "tw:relative tw:w-full tw:px-3 tw:flex tw:items-center tw:justify-center tw:transition-all tw:duration-300 tw:ease-out",
                isActive
                  ? "tw:h-[68px] tw:lg:h-[75px] tw:font-medium tw:text-[15px] tw:lg:text-[16px] tw:text-[#282828]"
                  : "tw:h-[54px] tw:lg:h-[60px] tw:text-[14px] tw:lg:text-[15px] tw:leading-[22px] tw:text-[#8D8D8D] tw:hover:text-[#4E4E4E]"
              )}
            >
              {t(`meetAssistInquiry.Journeys.${journey}`)}
            </span>
          </button>
        );
      })}
    </div>
  );
};

/** Hairline under a section or card title. */
export const Divider = ({ className }) => <div className={cn("tw:h-px tw:w-full tw:bg-[#F1F1F1]", className)} />;

/**
 * White card holding one part of the form: title (+ optional intro line),
 * a hairline, then the fields. Fades up the first time it scrolls into view;
 * pass `shouldReveal={false}` for a card that must be visible immediately.
 */
export const FormCard = ({ title, intro, children, className, shouldReveal = true }) => {
  const cardClassName = cn(
    "tw:bg-white tw:border tw:border-solid tw:border-[#F1F1F1] tw:!rounded-[5px] tw:px-5 tw:lg:px-7 tw:py-[30px] tw:lg:py-8 tw:flex tw:flex-col tw:gap-y-7",
    className
  );

  const cardContent = (
    <>
      <div className="tw:flex tw:flex-col tw:gap-y-[6px] tw:font-hanken-grotesk">
        {/* A div with heading semantics: the site's global h2 styles (custom.css,
            Bootstrap) would otherwise override this size and weight. */}
        <div role="heading" aria-level={2} className="tw:font-semibold tw:text-[16px] tw:leading-[21px] tw:lg:text-[18px] tw:lg:leading-[25px] tw:text-[#282828]">
          {title}
        </div>
        {intro && (
          <p className="tw:!mb-0 tw:text-[13px] tw:leading-[18.5px] tw:lg:text-[14px] tw:lg:leading-[20px] tw:!text-[rgba(78,78,78,0.87)]">
            {intro}
          </p>
        )}
      </div>
      <Divider />
      {children}
    </>
  );

  if (!shouldReveal) return <section className={cardClassName}>{cardContent}</section>;
  return (
    <Reveal as="section" y={20} className={cardClassName}>
      {cardContent}
    </Reveal>
  );
};

/**
 * Grey box of "※" notes, used for disclaimers and departure tips.
 * Pass padding with `!` (e.g. `tw:!p-5`): Bootstrap's `ul` padding is not
 * in a CSS layer, so a plain Tailwind padding class loses to it.
 */
export const NoteList = ({ notes, className }) => (
  <ul
    className={cn(
      "tw:!mb-0 tw:list-none tw:bg-[#F9F9F9] tw:!rounded-[5px] tw:flex tw:flex-col tw:gap-y-[6px] tw:font-hanken-grotesk tw:text-[#4E4E4E]",
      className
    )}
  >
    {notes.map((note) => (
      <li key={note} className="tw:flex tw:gap-x-[5px]">
        <span aria-hidden="true">※</span>
        <span>{note}</span>
      </li>
    ))}
  </ul>
);
